import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Box
} from '@mui/material';
import {
  Construction as BuildIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useThemeSettings } from '../../../../contexts/ThemeContext';
import { formatDate } from '../../utils/ticketTransformers';

/**
 * Dialog for selecting from multiple interventions
 */
const InterventionSelectionDialog = ({
  open,
  onClose,
  onSelect,
  interventions = []
}) => {
  const { t } = useThemeSettings();

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'in_progress':
        return 'warning';
      case 'scheduled':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return t ? t('Tickets.status_completed') : 'Completed';
      case 'cancelled':
        return t ? t('Tickets.status_cancelled') : 'Cancelled';
      case 'in_progress':
        return t ? t('Tickets.status_in_progress') : 'In Progress';
      case 'scheduled':
        return t ? t('Tickets.status_scheduled') : 'Scheduled';
      case 'pending':
        return t ? t('Tickets.status_pending') : 'Pending';
      default:
        return status;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {t ? t("Tickets.select_intervention") : "Select Intervention Report"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t ? t("Tickets.multiple_interventions_message") : "This ticket has multiple interventions. Please select which one you would like to view:"}
        </DialogContentText>
        <List>
          {interventions.map((intervention) => (
            <ListItem
              key={intervention.id}
              button
              onClick={() => onSelect(intervention)}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                <BuildIcon color={intervention.status === 'completed' ? 'success' : 'primary'} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>
                      {t ? t('Tickets.intervention') : 'Intervention'} #{intervention.id}
                    </span>
                    <Chip
                      label={getStatusLabel(intervention.status)}
                      size="small"
                      color={getStatusColor(intervention.status)}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <EventIcon fontSize="small" color="action" />
                    <span>
                      {intervention.scheduled_for || intervention.scheduledFor
                        ? formatDate(intervention.scheduled_for || intervention.scheduledFor)
                        : (t ? t('Tickets.not_scheduled') : 'Not scheduled')}
                    </span>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {t ? t("Common.close") : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterventionSelectionDialog;
